import { useCallback } from 'react';

import type { PendingProjectInvite } from '../types';

// Pending-project-invite fetcher used by EmailOtpStep after the user
// verifies their email and by SelfServiceSMBSignupModal on resume.
//
// Unlike useDomainMatch (whose groupsByEmailDomain call omits
// otpVerificationToken and relies solely on Bearer auth from the
// post-signup session), this hook threads an OTP-derived identity through
// to the server too: pendingProjectInvite accepts either an OTP
// verification token or Bearer auth. Both call sites already hold a
// Bearer token by the time they call this (AccountStep's signup() already
// authenticated; the resume path is authenticated by definition) -- the
// OTP token here is belt-and-braces, not the primary credential.

export type { PendingProjectInvite };

export type PendingProjectInviteRunQuery = (
  otpVerificationToken?: string
) => Promise<PendingProjectInvite | null>;

export interface UsePendingProjectInviteOptions {
  runQuery: PendingProjectInviteRunQuery;
}

export interface UsePendingProjectInviteResult {
  fetchPendingInvite: (otpVerificationToken?: string) => Promise<PendingProjectInvite | null>;
}

export function usePendingProjectInvite({
  runQuery,
}: UsePendingProjectInviteOptions): UsePendingProjectInviteResult {
  const fetchPendingInvite = useCallback(
    async (otpVerificationToken?: string) => runQuery(otpVerificationToken),
    [runQuery]
  );

  return { fetchPendingInvite };
}
